import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Loader2, Search, Trash2, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/** Scan → review → confirm. Nothing is deleted until the admin ticks the rows
 *  and types DELETE, and the backend refuses any account with order history. */
export const TestDataCleanup = () => {
    const [patterns, setPatterns] = useState("test,qa,demo,dummy");
    const [before, setBefore] = useState("");
    const [scan, setScan] = useState(null);
    const [picked, setPicked] = useState({});
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const qs = new URLSearchParams({ patterns });
            if (before) qs.set("inactive_before", `${before}T00:00:00+00:00`);
            const { data } = await api.get(`/admin/cleanup/test-accounts?${qs}`);
            setScan(data);
            // Pattern matches are pre-ticked. Date-only matches are NOT — those
            // could be genuine customers who simply haven't ordered yet.
            setPicked(Object.fromEntries(data.candidates.map((c) => [c.id, c.matched_pattern])));
            setConfirm("");
            toast.success(`${data.summary.deletable} account(s) can be cleaned · ${data.summary.held_back} held back`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Scan failed");
        } finally {
            setBusy(false);
        }
    };

    const chosen = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);

    const destroy = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/cleanup/test-accounts", { user_ids: chosen, confirm });
            setResult(data);
            toast.success(`${data.deleted_count} account(s) deleted`);
            await run();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cleanup failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="test-data-cleanup">
            <div className="bg-amber-50 border border-amber-300 p-4 text-xs text-amber-900 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                    <strong>Review the list before confirming.</strong> Accounts with any orders or appointments are
                    never deleted — they hold billing history. Only <em>customer</em> logins are in scope; staff,
                    distributor, doctor and admin accounts are untouched. Date-only matches start unticked because a
                    real customer may simply not have ordered yet.
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">Email contains (comma separated)</div>
                    <Input value={patterns} onChange={(e) => setPatterns(e.target.value)} className="rounded-none" data-testid="cleanup-patterns" />
                </div>
                <div>
                    <div className="label-eyebrow text-stone-500 mb-1">Also flag never-ordered accounts created before</div>
                    <Input type="date" value={before} onChange={(e) => setBefore(e.target.value)} className="rounded-none" data-testid="cleanup-before-date" />
                </div>
            </div>

            <Button onClick={run} disabled={busy} variant="outline" className="rounded-none border-stone-400" data-testid="cleanup-scan-btn">
                {busy && !scan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Clean Test Data — scan first
            </Button>

            {result && (
                <div className="bg-emerald-50 border border-emerald-300 p-4 text-sm" data-testid="cleanup-result">
                    <div className="flex items-center gap-2 font-medium text-emerald-900">
                        <CheckCircle2 className="w-4 h-4" />
                        Deleted {result.deleted_count} account(s) and {result.related_rows_removed} linked record(s).
                    </div>
                    {result.refused?.length > 0 && (
                        <ul className="mt-2 text-xs text-stone-700 list-disc pl-5">
                            {result.refused.map((r) => <li key={r.id}>Kept <strong>{r.email}</strong> — {r.reason}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {scan && (
                <>
                    <div className="text-xs text-stone-600" data-testid="cleanup-summary">
                        {scan.summary.total_customers} customer accounts scanned · {scan.summary.matched} matched ·
                        <strong> {scan.summary.deletable} deletable</strong> · {scan.summary.held_back} held back for records.
                    </div>

                    {scan.candidates.length === 0 ? (
                        <div className="bg-white border border-stone-200 p-6 text-sm text-stone-500" data-testid="cleanup-none">
                            Nothing matches these criteria. Your customer list is clean.
                        </div>
                    ) : (
                        <div className="bg-white border border-stone-200 overflow-x-auto">
                            <div className="px-4 py-2 border-b border-stone-100 flex items-center gap-3 text-xs">
                                <button onClick={() => setPicked(Object.fromEntries(scan.candidates.map((c) => [c.id, true])))} className="text-sky-700 hover:underline" data-testid="cleanup-select-all">Select all</button>
                                <button onClick={() => setPicked({})} className="text-stone-500 hover:underline" data-testid="cleanup-select-none">Clear</button>
                                <span className="ml-auto text-stone-500">{chosen.length} selected</span>
                            </div>
                            <table className="w-full text-sm" data-testid="cleanup-table">
                                <thead className="bg-stone-50 text-left text-xs uppercase tracking-widest text-stone-500">
                                    <tr>
                                        <th className="p-3 w-10"></th>
                                        <th className="p-3">Account</th>
                                        <th className="p-3">Joined</th>
                                        <th className="p-3">Why it matched</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {scan.candidates.map((c) => (
                                        <tr key={c.id} className="border-t border-stone-100" data-testid={`cleanup-row-${c.id}`}>
                                            <td className="p-3">
                                                <Checkbox
                                                    checked={!!picked[c.id]}
                                                    onCheckedChange={(v) => setPicked((p) => ({ ...p, [c.id]: !!v }))}
                                                    data-testid={`cleanup-check-${c.id}`}
                                                />
                                            </td>
                                            <td className="p-3">
                                                <div className="font-mono text-xs text-stone-900">{c.email}</div>
                                                <div className="text-xs text-stone-500">{c.name || "—"}{c.phone ? ` · ${c.phone}` : ""}</div>
                                            </td>
                                            <td className="p-3 text-xs text-stone-500">{new Date(c.created_at).toLocaleDateString()}</td>
                                            <td className="p-3 text-xs">
                                                {c.reasons.map((r) => (
                                                    <span key={r} className={`inline-block mr-1 mb-1 px-1.5 py-0.5 border text-[10px] ${c.matched_pattern ? "border-red-300 text-red-700" : "border-amber-400 text-amber-700"}`}>{r}</span>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {scan.held_back.length > 0 && (
                        <div className="bg-white border border-stone-200" data-testid="cleanup-held-back">
                            <div className="px-4 py-2 border-b border-stone-100 text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                                Matched but kept — these have history
                            </div>
                            <ul className="p-4 space-y-1 text-xs text-stone-700">
                                {scan.held_back.map((h) => (
                                    <li key={h.id} data-testid={`cleanup-kept-${h.id}`}>
                                        <span className="font-mono">{h.email}</span> — {h.blockers.join(", ")}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {chosen.length > 0 && (
                        <div className="bg-red-50 border border-red-300 p-4 space-y-3" data-testid="cleanup-confirm-box">
                            <div className="text-sm text-red-900">
                                This permanently deletes <strong>{chosen.length} account(s)</strong> plus their linked
                                CRM contacts, saved carts, reviews, subscriptions and Mailchimp records. It cannot be undone.
                            </div>
                            <div className="flex gap-2 items-end flex-wrap">
                                <div>
                                    <div className="label-eyebrow text-red-800 mb-1">Type DELETE to confirm</div>
                                    <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="rounded-none w-44 bg-white" placeholder="DELETE" data-testid="cleanup-confirm-input" />
                                </div>
                                <Button
                                    onClick={destroy}
                                    disabled={busy || confirm !== "DELETE"}
                                    className="rounded-none bg-red-700 hover:bg-red-800 text-white"
                                    data-testid="cleanup-delete-btn"
                                >
                                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                                    Delete {chosen.length} account(s)
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
